import isWithinInterval from 'date-fns/isWithinInterval';
import { graphql, HeadProps, PageProps } from 'gatsby';
import React, { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { BranchContactBlade } from '../../components/branches/BranchContactBlade';
import { BranchDivisionSummary } from '../../components/branches/BranchDivisionDetails';
import { Card } from '../../components/Card';
import { Divider } from '../../components/Divider';
import { Grid } from '../../components/Grid';
import HtmlContent from '../../components/Html';
import { ImageCard } from '../../components/ImageCard';
import { LocationActionSection } from '../../components/locations/LocationActionSection';
import { MapMarker } from '../../components/Map';
import { PageBackButton } from '../../components/PageBackButton';
import { Hero } from '../../components/pageHeader/hero/Hero';
import { RecentPropertiesCarousel } from '../../components/properties/RecentPropertiesCarousel';
import { RequestAValuationBlade } from '../../components/properties/RequestAValuationBlade';
import { Section } from '../../components/Section';
import { SEO } from '../../components/Seo';
import { Spacer } from '../../components/Spacer';
import { TickListWithDescription } from '../../components/TickListWithDescription';
import { Trustpilot } from '../../components/Trustpilot';
import { Typography } from '../../components/Typography';
import { UiStateContext } from '../../context/UiStateContext';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { Kfh_BranchType } from '../../types/graphqlTypes';
import { BRANCH_URLS, PROPERTY_URLS } from '../../urls';
import { isBrowser } from '../../utils';
import { ReviewCarousel } from '../../components/ReviewCarousel';
import { ReviewPanels } from '../../components/pageHeader/hero/components/ReviewPanels';

interface BranchPageProps {
  branch: Kfh_BranchType;
  rentalProperties: any;
  resaleProperties: any;
  settings: any;
  isActive?: boolean;
}

interface TransformedReview {
  type: 'trustpilot' | 'google';
  text: string;
  rating: number;
  url: string;
  date: string; // Human-readable format
}

const StyledAreaGuideCard = styled(ImageCard)`
  ${({ theme: { maxMedia } }) => css`
    // Mobile and Tablet < styles

    @media (max-width: ${maxMedia.xl}) {
      display: none;
    }
  `};
`;

const StyledUspDivider = styled(Divider)`
  ${({ theme: { maxMedia } }) => css`
    // Mobile and Tablet < styles

    @media (max-width: ${maxMedia.xl}) {
      display: none;
    }
  `};
`;

const BranchPage = ({ data }: PageProps<BranchPageProps>) => {
  const { activeBlade, setActiveBlade } = useContext(UiStateContext);
  const [activeEnquiryType, setActiveEnquiryType] = useState<'Sales' | 'Lettings'>('Sales');
  const { branch, settings, rentalProperties, resaleProperties } = data;
  const { dataLayerPush } = useDataLayerPush();
  const areaRentalProperties = branch?.area?.properties.sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  );

  const {
    name,
    businessProfile,
    salesManager,
    lettingsManager,
    seo,
    googleMapLinkRental,
    googleMapLinkResales,
    openingHours,
    area,
    shortDescription,
    mediaSet,
    trustPilotReviews,
    googleReviews,
    googleLocation,
    googleMyBusinessUrl,
  } = branch;
  const salesContactDetails = businessProfile?.salesContactDetails;
  const lettingsContactDetails = businessProfile?.lettingsContactDetails;

  const branchSearchSaleUrl = `/search-results/?multiSearch=business%3D${businessProfile?.businessId}`;
  const branchSearchRentUrl = `/search-results/?multiSearch=business%3D${businessProfile?.businessId}&category=RENTAL`;

  // Branch image

  const branchImage = mediaSet?.media?.find((mediaItem) => mediaItem.role === 'Large')?.media?.localFile;

  // Sales Manager Image

  const salesManageImage = salesManager?.mediaSet?.media?.find((mediaItem) => mediaItem.role === 'Profile')?.media
    ?.localFile;

  // Lettings Manager Image

  const lettingsManageImage = lettingsManager?.mediaSet?.media?.find((mediaItem) => mediaItem.role === 'Profile')?.media
    ?.localFile;

  // Area guide image

  const areaGuideImage = area?.mediaSet?.media[0]?.media;

  const mapMarkers: MapMarker[] = [
    {
      id: branch.id,
      position: {
        lat: branch.salesLat,
        lng: branch.salesLon,
      },
    },
  ];

  if (
    branch.lettingsLat &&
    branch.lettingsLon &&
    (branch.salesLat !== branch.lettingsLat || branch.salesLon !== branch.lettingsLon)
  ) {
    mapMarkers.push({
      id: branch.id,
      position: {
        lat: branch.lettingsLat,
        lng: branch.lettingsLon,
      },
    });
  }

  // Blanket message / special opening times

  const startDate = settings?.branchBlanketMessageStart as Date;
  const endDate = settings?.branchBlanketMessageEnd as Date;
  let displayBlanketMessage = false;

  if (isBrowser) {
    displayBlanketMessage = isWithinInterval(new Date(), {
      start: new Date(startDate),
      end: new Date(endDate),
    });

    // console.log('displayBlanketMessage', displayBlanketMessage);
  }

  const defaultMultisearch =
    area?.artirixMultiSearch && area?.artirixDisplayName
      ? {
          name: area.artirixDisplayName,
          multiSearch: area.artirixMultiSearch,
        }
      : {};

  const branchTitle = (title) => {
    if (title.includes('New Homes')) {
      return title.replace('estate agents', '');
    }
    return title;
  };

  const uspBranchData = [
    {
      headline: 'Network',
      text: 'Our 60+ London-wide branches bring hundreds of new properties to the market each week.',
    },
    {
      headline: 'Experience',
      text: 'Our branch directors have an average length of service of over 10 years delivering results for our clients.',
    },
    {
      headline: 'Results',
      text: 'We sold over £2.5 billion worth of property in 2024, making us experts in the London market.',
    },
    {
      headline: 'Reach',
      text: `With 14,000 active tenancies, we're one of London's largest lettings operations.`,
    },
  ];

  // Reviews

  // Helper function to format the date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
  };

  // Transform Trustpilot reviews

  const transformedTrustpilotReviews: TransformedReview[] = trustPilotReviews?.map((review) => ({
    type: 'trustpilot',
    text: review?.text,
    rating: review?.stars,
    url: review?.trustPilotUrl || 'https://uk.trustpilot.com/review/www.kfh.co.uk',
    date: formatDate(review?.createdAt),
  }));

  // Transform Google reviews

  const transformedGoogleReviews: TransformedReview[] = googleReviews?.map((review) => ({
    type: 'google',
    text: review?.text,
    rating: review?.stars,
    url: googleMyBusinessUrl,
    date: formatDate(review?.createdAt),
  }));

  // Combine and sort reviews
  const combinedReviews: TransformedReview[] = [
    ...(transformedTrustpilotReviews || []),
    ...(transformedGoogleReviews || []),
  ]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by most recent
    .slice(0, 18); // Take the top 18

  return (
    <>
      <PageBackButton url={`/${BRANCH_URLS.branches}`} label="all branches" />

      <Hero
        trustpilot
        heading={branchTitle(seo?.h1 ?? branch.name)}
        lead={shortDescription}
        button={
          !branch.name.includes('New Homes') && {
            variant: 'solid',
            label: 'Request a free valuation',
            as: 'button',
            type: 'button',
            onClick: () => setActiveBlade('propertyRequestAValuation'),
          }
        }
        backgroundImage={branchImage}
        googleReviewsLink={googleMyBusinessUrl}
        googleReviewsRating={googleLocation?.averageRating}
        customComponent={<ReviewPanels googleRating={googleLocation?.averageRating} googleUrl={googleMyBusinessUrl} />}
        hasReviewPanel
      />

      <Section contained background="white" id="section">
        <TickListWithDescription tickListListing={uspBranchData} />
      </Section>

      {/* Branch details */}
      <Section contained id="section">
        <Spacer responsive>
          <StyledUspDivider />
          {/* Contact details */}
          <Grid desktopColumns="3">
            {branch.isSales && (
              <BranchDivisionSummary
                contactDetails={salesContactDetails}
                type="sales"
                mapLink={googleMapLinkResales}
                name={name}
                businessId={branch?.businessProfile?.businessId}
                contactCTA
                contactCTAOnClick={() => {
                  setActiveBlade('branchContact');
                  setActiveEnquiryType('Sales');
                }}
              />
            )}
            {branch.isLettings && (
              <BranchDivisionSummary
                contactDetails={lettingsContactDetails}
                type="lettings"
                mapLink={googleMapLinkRental}
                name={name}
                businessId={branch?.businessProfile?.businessId}
                contactCTA
                isLettingsOverride={branch?.isLettingsOverride}
                isLettingsOverrideAreaRelativeUrl={branch?.lettingsOverrideArea?.relativeUrl}
                contactCTAOnClick={() => {
                  setActiveBlade('branchContact');
                  setActiveEnquiryType('Lettings');
                }}
              />
            )}
            <Spacer>
              <Spacer spacing="half">
                <Typography component="h2" variant="titleMedium">
                  Opening times
                </Typography>
                {openingHours && <HtmlContent>{openingHours}</HtmlContent>}
              </Spacer>

              {settings?.branchBlanketMessage && displayBlanketMessage ? (
                <>
                  <Divider />
                  <HtmlContent>{settings.branchBlanketMessage}</HtmlContent>
                </>
              ) : (
                ''
              )}
            </Spacer>
          </Grid>
          {(salesManager || lettingsManager) && <Divider />}
          {/* Profiles details */}
          <Grid desktopColumns="3">
            {salesManager && (
              <>
                <Card
                  heading={salesManager.fullName}
                  lead={salesManager.position ? salesManager.position : 'Sales'}
                  actionLabel="Arrange a sales valuation"
                  onClick={() => {
                    setActiveBlade('propertyRequestAValuation');
                    setActiveEnquiryType('Sales');
                    dataLayerPush({
                      event: 'cta button',
                      clickText: 'Arrange a sales valuation',
                      clickSection: undefined,
                      clickBladeId: 'propertyRequestAValuation',
                      clickPath: undefined,
                      clickHostname: undefined,
                      clickQueryString: undefined,
                    });
                  }}
                  image={salesManageImage}
                />
              </>
            )}
            {lettingsManager && (
              <Card
                heading={lettingsManager.fullName}
                lead={lettingsManager.position ? lettingsManager.position : 'Lettings'}
                actionLabel="Arrange a lettings valuation"
                onClick={() => {
                  setActiveBlade('propertyRequestAValuation');
                  setActiveEnquiryType('Lettings');
                  dataLayerPush({
                    event: 'cta button',
                    clickText: 'Arrange a lettings valuation',
                    clickSection: undefined,
                    clickBladeId: 'propertyRequestAValuation',
                    clickPath: undefined,
                    clickHostname: undefined,
                    clickQueryString: undefined,
                  });
                }}
                image={lettingsManageImage}
              />
            )}
            {branch.area?.relativeUrl && (
              <StyledAreaGuideCard
                url={branch.area?.relativeUrl}
                image={areaGuideImage}
                heading={`${area.name} <br/>${area.region.name}`}
                actionLabel="Learn more"
                tag="Guide"
                vertical
              />
            )}
          </Grid>
        </Spacer>
      </Section>
      <RecentPropertiesCarousel
        rentalProperties={(rentalProperties.edges?.length > 0 ? rentalProperties?.edges : areaRentalProperties)?.slice(
          0,
          6
        )}
        resaleProperties={resaleProperties.edges?.slice(0, 6)}
        searchRentUrl={
          branch.isLettingsOverride
            ? `${branch?.lettingsOverrideArea?.relativeUrl}${PROPERTY_URLS.propertiesForRent}`
            : branchSearchRentUrl
        }
        searchSaleUrl={branchSearchSaleUrl}
        searchType={branch.name}
      />

      {/* Action section */}
      <LocationActionSection defaultMultisearch={defaultMultisearch} />

      {/* Reviews */}
      <ReviewCarousel items={combinedReviews} heading="What our customers say" />

      {/* Contact blade */}
      {activeBlade === 'branchContact' && <BranchContactBlade branch={branch} enquiryType={activeEnquiryType} />}

      {/* Valuation blade*/}

      <RequestAValuationBlade enquiryType={activeEnquiryType} />
    </>
  );
};

export default BranchPage;

type AggregateRatingSchema = {
  '@context': string;
  '@type': 'AggregateRating';
  itemReviewed: {
    '@type': 'LocalBusiness';
    name: string;
    telephone?: string;
    address: {
      '@type': 'PostalAddress';
      streetAddress?: string;
      addressLocality?: string;
      addressRegion?: string;
      postalCode?: string;
      addressCountry: string;
    };
  };
  author: {
    '@type': 'Organization';
    name: string;
  };
  ratingValue?: string;
  reviewCount?: string;
};

const generateSchema = (branch: Kfh_BranchType, settings) => {
  const schemas: AggregateRatingSchema[] = [];

  // Google Reviews Schema
  if (branch.googleLocation) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      itemReviewed: {
        '@type': 'LocalBusiness',
        name: branch.name,
        telephone: branch.businessProfile?.salesContactDetails?.telephone || '',
        address: {
          '@type': 'PostalAddress',
          streetAddress: branch.businessProfile?.salesContactDetails?.streetname || '',
          addressLocality: branch.businessProfile?.salesContactDetails?.city || '',
          addressRegion: branch.businessProfile?.salesContactDetails?.state || '',
          postalCode: branch.businessProfile?.salesContactDetails?.postcode || '',
          addressCountry: 'UK',
        },
      },
      author: {
        '@type': 'Organization',
        name: 'Google Business',
      },
      ratingValue: branch.googleLocation?.averageRating || '0',
      reviewCount: branch.googleLocation?.totalReviews || '0',
    });
  }

  // Trustpilot Reviews Schema
  if (settings.trustPilotScore) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      itemReviewed: {
        '@type': 'LocalBusiness',
        name: 'Kinleigh, Folkard and Hayward Estate Agents',
        telephone: '020 8131 9775',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'KFH House, 5 Compton Road',
          addressLocality: 'Wimbledon',
          addressRegion: 'London',
          postalCode: 'SW19 7QA',
          addressCountry: 'UK',
        },
      },
      author: {
        '@type': 'Organization',
        name: 'TrustPilot',
      },
      ratingValue: settings?.trustPilotScore.trustScore,
      reviewCount: settings?.trustPilotScore.total,
    });
  }

  return JSON.stringify(schemas, null, 2);
};

export const Head = ({ data }: HeadProps<BranchPageProps>) => {
  const { seo } = data.branch;

  return (
    <>
      <SEO data={seo} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: generateSchema(data.branch, data.settings) }}
      />
    </>
  );
};
export const pageQuery = graphql`
  query BranchPagePageQuery($id: String!, $businessId: Int!, $areaRentalPropertyIds: [String]) {
    branch: kfhBranchType(id: { eq: $id }) {
      ...BranchDetails
      area {
        properties(ids: $areaRentalPropertyIds) {
          ...PropertyListingProperties
        }
      }
    }
    settings: kfhKfhSettingsType {
      branchBlanketMessage
      branchBlanketMessageEnd
      branchBlanketMessageStart
      trustPilotScore {
        stars
        total
        trustScore
      }
    }
    resaleProperties: allKfhResidentialResalePropertyType(
      limit: 6
      filter: { businessId: { eq: $businessId }, available: { eq: true } }
      sort: { createdDate: DESC }
    ) {
      edges {
        node {
          ...PropertyListingProperties
        }
      }
    }
    rentalProperties: allKfhResidentialRentalPropertyType(
      limit: 6
      filter: { businessId: { eq: $businessId }, available: { eq: true }, sellingState: { ne: "Under offer" } }
      sort: { createdDate: DESC }
    ) {
      edges {
        node {
          ...PropertyListingProperties
        }
      }
    }
  }
`;
